/*
This file is located in the main branch
Last time the file was updated (UTC): [05.09.2024 11:48:30]
Permissions to update outside the main branch: [false]
*/

import {
    Button,
    Form,
    minRule,
    Modal,
    requiredRule,
    Tab,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { datetimeWithSecondsFormat } from 'constans/dateFormat';
import { format, parseISO } from 'date-fns';
import useAuth from 'hooks/useAuth';
import { LeadgenInfo } from 'models/api/leadgen';
import { Dispatch, Key, SetStateAction, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, TabContent } from 'reactstrap';
import 'scss/Main.scss';
import '../Leadgen.scss';
import { useLeadgenStatsWorkplaceState } from '../LeadgenStats';
import LeadgenModalPostbackParameters from './LeadgenModalPostbackParameters';
import LeadgenModalReferallinks from './LeadgenModalReferallinks';
import LeadgenModalTarificationParameters from './LeadgenModalTarificationParameters';
import LeadgenModalUtmParams, { emptyItem as emptyUtmParam } from './LeadgenModalUtmParams';

const initialItem: LeadgenInfo = {
    name: '',
    dsc: '',
    isActive: false,
    lifetime: undefined,
    successRegExp: '',
    urlBase: '',
    postbackParameters: [],
    referrallinks: [],
    utmParams: [
        {
            ...emptyUtmParam,
            paramName: 'leadname_id',
            paramCode: 'transaction_id',
        },
    ],
    tarificationParameters: [],
};

type LeadgenModalProps = {
    isOpen: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
};

const LeadgenModal = ({ isOpen = false, toggle }: LeadgenModalProps) => {
    const { hasRole } = useAuth();
    const className = 'leadgen-modal';
    const { t } = useTranslation();
    const { currentItem, changeItemMutation, addItemMutation } = useLeadgenStatsWorkplaceState();
    const [activeTab, setActiveTab] = useState('main');
    const modalRef = useRef(null);

    const [leadgen, setLeadgen] = useState(currentItem || initialItem);
    useEffect(() => setLeadgen({ ...initialItem, ...currentItem }), [currentItem]);

    const saveChanges = (data: LeadgenInfo) => {
        data.tarificationParameters.forEach((tp) => {
            const ed = tp.ed ? parseISO(tp.ed) : new Date();
            ed.setSeconds(59);
            tp.ed = format(ed, datetimeWithSecondsFormat);
        });

        data.postbackParameters.forEach((pb) => {
            if (!pb.applyLimit) {
                pb.limitMin = null;
                pb.limitMax = null;
            }
        });

        (leadgen.id ? changeItemMutation : addItemMutation).mutate(data, {
            onSuccess: () => closeModal(),
        });
    };

    const closeModal = () => {
        toggle(false);
    };

    const formHasRole = (addRole: string, editRole: string): boolean =>
        leadgen.id ? hasRole(editRole) : hasRole(addRole);

    const toggleTab = (value?: Key) => {
        setActiveTab(value?.toString() ?? '');
    };

    return (
        <Modal
            size="lg"
            className={className}
            isOpen={isOpen}
            toggle={closeModal}
            scrollable
            innerRef={modalRef}
            data-testid="leadgen-modal"
        >
            <Form<LeadgenInfo>
                className="form-in-modal"
                onSubmit={(data) => saveChanges(data)}
                defaultValues={leadgen}
                clearAfterSubmit={false}
            >
                <ModalHeader toggle={closeModal}>
                    {leadgen.id ? `#${leadgen.id}` : t('BackOfficeNew')}
                </ModalHeader>
                <ModalBody className="custom-scrollbar">
                    <Nav className={`${className}__nav`} tabs>
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('main')}
                                active={activeTab === 'main'}
                            >
                                {t('BackOfficeMainPageName')}
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('postbackParameters')}
                                active={activeTab === 'postbackParameters'}
                            >
                                {t('BackOfficePostbackParameters')}
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('referrallinks')}
                                active={activeTab === 'referrallinks'}
                            >
                                {t('BackOfficeReferralLinks')}
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('utmParams')}
                                active={activeTab === 'utmParams'}
                            >
                                {t('BackOfficeUtmParams')}
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('tarificationParameters')}
                                active={activeTab === 'tarificationParameters'}
                            >
                                {t('BackOfficeTarificationParameters')}
                            </NavLink>
                        </NavItem>
                    </Nav>
                    <TabContent activeTab={activeTab}>
                        <Tab toggle={toggleTab} tabId="main">
                            <ValidatedInput
                                type="checkbox"
                                name="isActive"
                                label={t('BackOfficeIsActive')}
                                disabled={true}
                            />
                            <hr />
                            <ValidatedInput
                                name="name"
                                label={t('BackOfficeName')}
                                rules={{
                                    required: requiredRule,
                                }}
                                disabled={currentItem?.isActive}
                            />
                            <ValidatedInput
                                type="textarea"
                                name="dsc"
                                label={t('BackOfficeDescription')}
                                disabled={currentItem?.isActive}
                            />
                            <ValidatedInput
                                type="number"
                                name="lifetime"
                                label={t('BackOfficeLeadgenLifetime')}
                                rules={{
                                    required: requiredRule,
                                    min: minRule(1),
                                }}
                                disabled={currentItem?.isActive}
                            />
                        </Tab>
                        <Tab toggle={toggleTab} tabId="postbackParameters">
                            <LeadgenModalPostbackParameters
                                disabled={currentItem?.isActive || false}
                            />
                        </Tab>
                        <Tab toggle={toggleTab} tabId="referrallinks">
                            <LeadgenModalReferallinks disabled={currentItem?.isActive || false} />
                        </Tab>
                        <Tab toggle={toggleTab} tabId="utmParams">
                            <LeadgenModalUtmParams disabled={currentItem?.isActive || false} />
                        </Tab>
                        <Tab toggle={toggleTab} tabId="tarificationParameters">
                            <LeadgenModalTarificationParameters
                                disabled={currentItem?.isActive || false}
                                modalRef={modalRef}
                            />
                        </Tab>
                    </TabContent>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" type="button" onClick={closeModal}>
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button
                        color="success"
                        type="submit"
                        disabled={
                            currentItem?.isActive || !formHasRole('leadgenCreate', 'leadgenUpdate')
                        }
                    >
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default LeadgenModal;
